/**
 * 接続状態モニタ(CLAUDE.md §4-3)
 *
 * navigator.onLine は「LAN に繋がっているか」しか見ておらず、島内の
 * 微弱電波では「onLine=true だが実際には通信できない」が頻発する。
 * よって軽量エンドポイントへの実疎通確認で確定させる。
 *
 * タイムアウトを 3 秒と短く切っているのは、微弱電波で長時間待って
 * UI をブロックしないため(§4-2)。
 */

export type ConnState = "online" | "offline" | "checking";

const PROBE_URL = "/api/ping";
const PROBE_TIMEOUT_MS = 3000;

/** 疎通確認の最短間隔。連打・イベント連続発火で叩き続けないための下限。 */
const MIN_PROBE_INTERVAL_MS = 5000;

let state: ConnState = navigator.onLine ? "checking" : "offline";
let lastProbeAt = 0;
let inflight: Promise<boolean> | null = null;

const listeners = new Set<(s: ConnState) => void>();

function setState(next: ConnState): void {
  if (state === next) return;
  state = next;
  for (const fn of listeners) fn(state);
}

export function getConnState(): ConnState {
  return state;
}

export function onConnChange(fn: (s: ConnState) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * 実疎通を確認する。
 *
 * ステータスコードは問わない(404 でもサーバーに届いた証拠なので online)。
 * API 実装前でも接続判定が機能するようにしてある。
 * 5xx はサーバー障害なので offline 扱いにし、同期キューを溜める側に倒す。
 */
export async function probe(force = false): Promise<boolean> {
  if (!navigator.onLine) {
    setState("offline");
    return false;
  }
  if (inflight) return inflight;
  if (!force && Date.now() - lastProbeAt < MIN_PROBE_INTERVAL_MS) {
    return state === "online";
  }

  setState("checking");
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);

  inflight = (async () => {
    try {
      const res = await fetch(PROBE_URL, {
        method: "HEAD",
        cache: "no-store",
        signal: ctrl.signal,
      });
      const ok = res.status < 500;
      setState(ok ? "online" : "offline");
      return ok;
    } catch {
      setState("offline");
      return false;
    } finally {
      clearTimeout(timer);
      lastProbeAt = Date.now();
      inflight = null;
    }
  })();

  return inflight;
}

/** 接続監視を開始する。アプリ起動時に一度だけ呼ぶ。 */
export function startConnMonitor(): void {
  addEventListener("online", () => void probe(true));
  addEventListener("offline", () => setState("offline"));

  // アプリ復帰時に確認する(§4-4 の同期トリガと揃える)。
  // 島内でポケットから出した瞬間に電波が入っている、という状況を拾うため。
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void probe();
  });

  void probe(true);
}
